import { useState } from "react";
import { X, Heart, Check } from "lucide-react";
import { api, getVisitorId, markInterestedLocal } from "../lib/api";
import { toast } from "sonner";

export default function InterestModal({ event, onClose, onSuccess }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (sending) return;
    if (!name.trim() || (!email.trim() && !phone.trim())) {
      toast.error("Just your name and one way to reach you.");
      return;
    }
    setSending(true);
    try {
      const { data } = await api.post(`/events/${event.id}/interested`, {
        visitor_id: getVisitorId(),
        name: name.trim(),
        email: email.trim() || null,
        phone: phone.trim() || null,
      });
      markInterestedLocal(event.id);
      setDone(true);
      onSuccess(data.interested_count);
      toast.success("You're on the list. We'll be in touch.");
    } catch {
      toast.error("Something went wrong. Try again in a moment.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      data-testid="interest-modal"
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4"
      style={{ background: "rgba(45, 36, 30, 0.45)", backdropFilter: "blur(6px)" }}
      onClick={onClose}
    >
      <div
        className="card-warm w-full max-w-md p-7 sm:p-9 relative"
        onClick={(e) => e.stopPropagation()}
        style={{ background: "var(--bg)" }}
      >
        <button
          onClick={onClose}
          aria-label="Close"
          data-testid="interest-modal-close"
          className="absolute top-4 right-4 btn-ghost"
        >
          <X size={20} />
        </button>

        {done ? (
          <div className="text-center py-6" data-testid="interest-success-state">
            <div
              className="mx-auto mb-5 flex items-center justify-center rounded-full"
              style={{
                width: 64, height: 64,
                background: "var(--surface)",
                color: "var(--primary)",
              }}
            >
              <Check size={32} />
            </div>
            <h3 className="font-serif text-3xl leading-tight">You're in.</h3>
            <p className="mt-3 text-sm" style={{ color: "var(--text-muted)" }}>
              We'll send the address and a small note from the host a day before.
            </p>
            <button onClick={onClose} className="btn-primary mt-7" data-testid="interest-success-close">
              Close
            </button>
          </div>
        ) : (
          <>
            <div className="label-eyebrow">RSVP · {event.city}</div>
            <h3 className="font-serif text-3xl mt-2 leading-tight">
              Save my seat at <em>{event.title}</em>
            </h3>
            <p className="mt-3 text-sm" style={{ color: "var(--text-muted)" }}>
              Hosts hand-confirm each seat. Drop a name and one way to reach you.
            </p>

            <form onSubmit={submit} className="mt-6 space-y-3">
              <input
                placeholder="Your name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                data-testid="interest-name"
                autoFocus
              />
              <input
                type="email"
                placeholder="Email (optional if you give a phone)"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="interest-email"
              />
              <input
                type="tel"
                placeholder="Phone (optional if you give an email)"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                data-testid="interest-phone"
              />
              <button
                type="submit"
                disabled={sending}
                className="btn-primary w-full justify-center mt-2"
                data-testid="interest-submit"
              >
                <Heart size={16} /> {sending ? "Saving…" : "Confirm I'm interested"}
              </button>
              <p className="text-xs text-center" style={{ color: "var(--text-muted)" }}>
                We only share this with the host of this gathering.
              </p>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
